import React, { ReactNode } from 'react'

import clsx from 'clsx'

import BuildConfig from '$extensionSrc/buildConfig'
import WarningIcon from '$extensionSrc/icons/warning-icon.svg'
import { RecommendationState } from '$extensionSrc/utils/enums'

import SettingIcon from './SettingIcon'

import './SettingRow.scss'

export default function SettingRow({
  currentValueDescription,
  isLessRelevant,
  isSettingDisabled,
  isSettingDisabledReason,
  isSettingFulfilled,
  isSettingNotUndoable,
  isSettingSelected,
  label,
  labelId,
  recommendationState,
  onSelectSetting,
  locked = false,
}: {
  currentValueDescription: ReactNode | null
  isLessRelevant: boolean
  isSettingDisabled: boolean
  isSettingDisabledReason: ReactNode | null
  isSettingFulfilled: boolean
  isSettingNotUndoable: boolean
  isSettingSelected: boolean
  label: ReactNode
  labelId: string
  recommendationState: RecommendationState
  onSelectSetting: () => void
  locked: boolean
}) {
  let labelTextColorClassName = isSettingDisabled ? 'darkest-gray' : 'black'
  if (recommendationState !== RecommendationState.INCOMPLETE && !locked) {
    labelTextColorClassName = 'darkest-gray'
  }

  const cantUndoIcon = (
    <div className="cant-undo-warning">
      <WarningIcon className="warning-icon" aria-label="Warning: can't undo" />
      <div className="tooltip-text body3 left">
        If you change your mind after updating, {BuildConfig.TENANT_NAME}
        <br />
        won't be able to undo changes to this setting
      </div>
    </div>
  )

  let settingDescription: ReactNode = null
  if (locked) {
    settingDescription = 'Current Setting: -'
  } else if (isSettingDisabled) {
    settingDescription = isSettingDisabledReason
  } else if (
    recommendationState === RecommendationState.UPDATED &&
    isSettingNotUndoable
  ) {
    settingDescription = `${BuildConfig.TENANT_NAME} can't undo this change`
  } else if (
    (recommendationState === RecommendationState.INCOMPLETE ||
      !isSettingSelected) &&
    currentValueDescription
  ) {
    if (isSettingNotUndoable && !isSettingDisabled) {
      settingDescription = (
        <>
          Current Setting: {cantUndoIcon} {currentValueDescription}
        </>
      )
    } else {
      settingDescription = <>Current Setting: {currentValueDescription}</>
    }
  } else if (isSettingNotUndoable) {
    settingDescription = 'This info will be permanently changed'
  }

  return (
    <div className="SettingRow d-flex flex-row">
      <SettingIcon
        disabled={isSettingDisabled}
        fulfilled={isSettingFulfilled}
        labelId={labelId}
        lessRelevant={isLessRelevant}
        locked={locked}
        onSelectSetting={onSelectSetting}
        recommendationState={recommendationState}
        selected={isSettingSelected}
      />
      <div className="setting-text-wrapper d-flex flex-column">
        <div
          className={clsx('setting-label', 'body2', labelTextColorClassName)}
          id={labelId}
        >
          {label}
        </div>
        {settingDescription ? (
          <div className="setting-description-wrapper">
            <div className="body3 dark-gray">{settingDescription}</div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
